import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}
export function SEO({
  title = 'PT Wahana Konstruksi Mandiri - Kontraktor Konstruksi Terpercaya',
  description = 'PT Wahana Konstruksi Mandiri (WKM) adalah perusahaan konstruksi terpercaya di Kalimantan dengan sertifikasi ISO 9001, 14001, 45001, 37001. Spesialisasi dalam konstruksi gedung, infrastruktur, dan BIM services.',
  keywords = 'konstruksi, kontraktor, WKM, Wahana Konstruksi Mandiri, konstruksi Kalimantan, ISO 9001, ISO 14001, ISO 45001, ISO 37001, BIM services, konstruksi gedung, infrastruktur',
  ogImage = 'https://cdn.magicpatterns.com/uploads/6iFxGnKDo7eRn71KoTcRtd/Company_Profile_PT_Wahana_Konstruksi_Mandiri-6.png',
  canonical = 'https://wkmkonstruksi.id',
  type = 'website',
  author = 'PT Wahana Konstruksi Mandiri',
  publishedTime,
  modifiedTime
}: SEOProps) {
  useEffect(() => {
    document.title = title;
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('googlebot', 'index, follow');
    updateMetaTag('bingbot', 'index, follow');
    updateMetaTag('language', 'Indonesian');
    updateMetaTag('geo.region', 'ID-KS');
    updateMetaTag('geo.placename', 'Tanah Bumbu');
    updateMetaTag('geo.position', '-3.8;115.5');
    updateMetaTag('ICBM', '-3.8, 115.5');
    updateMetaTag('revisit-after', '7 days');
    updateMetaTag('rating', 'general');
    updateMetaTag('distribution', 'global');
    updateMetaTag('theme-color', '#334e68');
    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    updateMetaTag('apple-mobile-web-app-title', 'PT WKM');
    updateMetaTag('application-name', 'PT Wahana Konstruksi Mandiri');
    updateMetaTag('msapplication-TileColor', '#334e68');
    updateMetaTag('msapplication-TileImage', ogImage);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', canonical, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', 'PT Wahana Konstruksi Mandiri Logo', true);
    updateMetaTag('og:site_name', 'PT Wahana Konstruksi Mandiri', true);
    updateMetaTag('og:locale', 'id_ID', true);
    updateMetaTag('og:locale:alternate', 'en_US', true);
    if (publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true);
    }
    if (modifiedTime) {
      updateMetaTag('article:modified_time', modifiedTime, true);
    }
    updateMetaTag('article:author', author, true);
    updateMetaTag('article:section', 'Construction', true);
    updateMetaTag('article:tag', keywords, true);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
    updateMetaTag('twitter:image:alt', 'PT Wahana Konstruksi Mandiri Logo');
    updateMetaTag('twitter:site', '@wkmkonstruksi');
    updateMetaTag('twitter:creator', '@wkmkonstruksi');
    updateMetaTag('fb:app_id', '1234567890');
    updateMetaTag('fb:pages', 'PT-Wahana-Konstruksi-Mandiri');
    updateMetaTag('pinterest:description', description);
    updateMetaTag('pinterest:image', ogImage);
    updateMetaTag('linkedin:owner', 'PT Wahana Konstruksi Mandiri');
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
    let alternateLinkID = document.querySelector('link[rel="alternate"][hreflang="id"]') as HTMLLinkElement;
    if (!alternateLinkID) {
      alternateLinkID = document.createElement('link');
      alternateLinkID.setAttribute('rel', 'alternate');
      alternateLinkID.setAttribute('hreflang', 'id');
      document.head.appendChild(alternateLinkID);
    }
    alternateLinkID.setAttribute('href', canonical);
    let alternateLinkEN = document.querySelector('link[rel="alternate"][hreflang="en"]') as HTMLLinkElement;
    if (!alternateLinkEN) {
      alternateLinkEN = document.createElement('link');
      alternateLinkEN.setAttribute('rel', 'alternate');
      alternateLinkEN.setAttribute('hreflang', 'en');
      document.head.appendChild(alternateLinkEN);
    }
    alternateLinkEN.setAttribute('href', canonical);
    let alternateLinkXDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]') as HTMLLinkElement;
    if (!alternateLinkXDefault) {
      alternateLinkXDefault = document.createElement('link');
      alternateLinkXDefault.setAttribute('rel', 'alternate');
      alternateLinkXDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(alternateLinkXDefault);
    }
    alternateLinkXDefault.setAttribute('href', canonical);
  }, [title, description, keywords, ogImage, canonical, type, author, publishedTime, modifiedTime]);
  return null;
}
interface StructuredDataProps {
  type: 'Organization' | 'LocalBusiness' | 'Article' | 'BreadcrumbList' | 'WebSite' | 'FAQPage';
  data: any;
}
export function StructuredData({
  type,
  data
}: StructuredDataProps) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': type,
    ...data
  };
  useEffect(() => {
    const scriptId = `structured-data-${type.toLowerCase()}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
  return null;
}
export function OrganizationSchema() {
  const data = {
    name: 'PT Wahana Konstruksi Mandiri',
    alternateName: 'WKM',
    url: 'https://wkmkonstruksi.id',
    logo: 'https://cdn.magicpatterns.com/uploads/6iFxGnKDo7eRn71KoTcRtd/Company_Profile_PT_Wahana_Konstruksi_Mandiri-6.png',
    description: 'Perusahaan konstruksi terpercaya dengan sertifikasi ISO 9001, 14001, 45001, 37001',
    foundingDate: '2021-12',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Jl. Kodeco KM 2 Ruko Jhonlin Indah Blok B No. 09',
      addressLocality: 'Tanah Bumbu',
      addressRegion: 'Kalimantan Selatan',
      postalCode: '72213',
      addressCountry: 'ID'
    },
    contactPoint: [{
      '@type': 'ContactPoint',
      telephone: '+62-21-5553-1569',
      contactType: 'customer service',
      email: 'info@wkmkonstruksi.id',
      availableLanguage: ['Indonesian', 'English'],
      areaServed: 'ID',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '17:00'
      }
    }, {
      '@type': 'ContactPoint',
      email: 'corsec@wkmkonstruksi.id',
      contactType: 'corporate secretary',
      availableLanguage: ['Indonesian', 'English']
    }],
    sameAs: ['https://instagram.com/wahana.konstruksimandiri', 'https://www.linkedin.com/company/pt-wahana-konstruksi-mandiri', 'https://www.facebook.com/wkmkonstruksi'],
    areaServed: {
      '@type': 'Country',
      name: 'Indonesia'
    },
    knowsAbout: ['Construction', 'Building Construction', 'Civil Engineering', 'Infrastructure Development', 'BIM Services', 'ISO 9001', 'ISO 14001', 'ISO 45001', 'ISO 37001'],
    slogan: 'Membangun Masa Depan dengan Integritas & Kualitas',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 50
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '44'
    }
  };
  return <StructuredData type="Organization" data={data} data-id="element-208" />;
}
export function LocalBusinessSchema() {
  const data = {
    name: 'PT Wahana Konstruksi Mandiri',
    image: 'https://cdn.magicpatterns.com/uploads/6iFxGnKDo7eRn71KoTcRtd/Company_Profile_PT_Wahana_Konstruksi_Mandiri-6.png',
    '@id': 'https://wkmkonstruksi.id',
    url: 'https://wkmkonstruksi.id',
    telephone: '+62-21-5553-1569',
    email: 'info@wkmkonstruksi.id',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Jl. Kodeco KM 2 Ruko Jhonlin Indah Blok B No. 09',
      addressLocality: 'Tanah Bumbu',
      addressRegion: 'Kalimantan Selatan',
      postalCode: '72213',
      addressCountry: 'ID'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -3.8,
      longitude: 115.5
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00'
    },
    paymentAccepted: 'Cash, Credit Card, Bank Transfer',
    currenciesAccepted: 'IDR'
  };
  return <StructuredData type="LocalBusiness" data={data} data-id="element-209" />;
}
export function WebSiteSchema() {
  const data = {
    '@id': 'https://wkmkonstruksi.id/#website',
    url: 'https://wkmkonstruksi.id',
    name: 'PT Wahana Konstruksi Mandiri',
    description: 'Perusahaan konstruksi terpercaya dengan sertifikasi ISO',
    publisher: {
      '@id': 'https://wkmkonstruksi.id/#organization'
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://wkmkonstruksi.id/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'id-ID'
  };
  return <StructuredData type="WebSite" data={data} data-id="element-210" />;
}
export function BreadcrumbSchema(items: Array<{
  name: string;
  url: string;
}>) {
  const data = {
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
  return <StructuredData type="BreadcrumbList" data={data} data-id="element-211" />;
}